import { faShare, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { colours } from '../../assets/colours';
import { Button } from '../../shared/components/button/Button';
import ChevronBackBtn from '../../shared/components/styledComponents/chevronBackBtn/ChevronBackBtn';
import WhitePopUpCTA from '../../shared/modal/whitePopUp/WhitePopUpCTA';
import WhitePopUpNoTitle from '../../shared/modal/whitePopUp/WhitePopUpNoTitle';
import { FriendAndFamily, FriendAndFamilyActiveProfile } from '../../types/models/user';
import smileyFace from '../images/smileyFace.png';

const colors = ['red', 'orange', 'yellow', 'green', 'blue'];

type ManageFnFRightMenuProps = {
  profile: FriendAndFamilyActiveProfile;
  hide: boolean;
  userFnF: FriendAndFamily[];
  saveChanges: (profile: FriendAndFamily) => void;
  removeUser: (user: FriendAndFamily) => void;
  generateLink: (name: string, colour: string) => void;
  goBack: () => void;
};

export function ManageFnFRightMenu({
  profile,
  hide,
  userFnF,
  saveChanges,
  removeUser,
  generateLink,
  goBack,
}: ManageFnFRightMenuProps) {
  const { t } = useTranslation();
  const [userProfile, setUserProfile] = useState<FriendAndFamily | undefined>(undefined);
  const [inputName, setInputName] = useState<string | undefined>(undefined);
  const [copied, setCopied] = useState<boolean>(false);
  const [action, setAction] = useState<string>(profile.action); // ["manage", "invite"]
  const [randomColour, setRandomColour] = useState<string | undefined>(undefined);
  const [openModal, setOpenModal] = useState<boolean>(false);

  const handleChange = (field, value) => {
    let valid = false;
    if (field === 'name') {
      const nameRegex = /^[a-zA-Z0-9\s.'-]*$/;
      valid = nameRegex.test(value);
      valid = value.length < 50 && valid;
    } else {
      valid = true;
    }
    if (valid) {
      setUserProfile((prevProfile) => ({
        ...prevProfile,
        [field]: value,
      }));
    }
  };

  useEffect(() => {
    if (profile.action === 'manage') {
      setUserProfile(profile.user);
      setInputName(profile.user.name);
      setAction(profile.action);
    } else if (profile.action === 'invite') {
      setAction(profile.action);
      setRandomColour(colors[Math.floor(Math.random() * 5)]);
      setUserProfile({ name: '', colour: '', status: 'pending' });
    }
  }, [profile, userFnF]);

  const saveToClipboard = () => {
    if (!userProfile) return;

    const content = `<p>${t('S128')} ${userProfile.token}. ${t(
      'S128A'
    )}<a href="https://app.championhealth.co.uk/code/${
      userProfile.token
    }"  target="_blank" rel="noopener noreferrer">https://app.championhealth.co.uk/code/${
      userProfile.token
    }</a>.</p>`;

    const dummy = document.createElement('p');
    dummy.innerHTML = content;
    document.body.appendChild(dummy);

    const range = document.createRange();
    range.setStartBefore(dummy);
    range.setEndAfter(dummy);

    const selection = window.getSelection();
    // First clear, in case the user already selected some other text
    selection.removeAllRanges();
    selection.addRange(range);

    document.execCommand('copy');
    document.body.removeChild(dummy);
    setCopied(true);
  };

  const colorsTranslatedNames = {
    red: t('R087'),
    orange: t('R088'),
    yellow: t('R089'),
    green: t('R090'),
    blue: t('R091'),
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <fieldset
      id="manage-fnf-right-menu"
      className="edit-field fs-exclude family_and_friends_container"
      aria-labelledby="edit-title"
    >
      {hide && (
        <div className="btn-cont ">
          <ChevronBackBtn onClick={() => goBack()} />
        </div>
      )}
      {openModal && (
        <WhitePopUpCTA
          offline={false}
          popupContents={{
            title: t('S007'),
            textArr: [
              { text: t('S020'), className: 'sub-heading' },
              {
                text: t('S021'),
                className: '',
              },
              {
                text: (
                  <>
                    {t('S022')}{' '}
                    <a
                      href="https://support.championhealth.co.uk/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('S023')}
                    </a>
                  </>
                ),
                className: '',
              },
            ],
            buttons: [
              {
                text: t('M050'),
                color: 'dark',
                onClick: () => {
                  setOpenModal(false);
                },
              },
              {
                text: t('T016'),
                color: 'pink',
                onClick: () => {
                  removeUser(userProfile);
                  setOpenModal(false);
                },
              },
            ],
          }}
          onClose={handleCloseModal}
        />
      )}
      {copied && (
        <WhitePopUpNoTitle
          popupContents={{
            title: t('S004'),
            text: [t('S129')],
          }}
          ctaBtn={t('T015')}
          handleSubmit={() => {
            setCopied(false);
          }}
          returnFocus
          dialogTitle={t('modal.fflink.copied')}
        />
      )}
      {profile.action && (
        <>
          <div className="edit-title centre">
            {action === 'invite' ? t('S004') : `${t('S016')} ${inputName}`}
          </div>
          <div className="cw">
            <div
              className="ff-cont"
              style={{
                backgroundColor: colours[userProfile?.colour || randomColour],
              }}
            >
              <img src={smileyFace} alt="smiley face" />
            </div>
            <fieldset className="color-row">
              <legend className="visuallyHidden">
                {t('profile.manageFnF.invitedUserAvatarColor')}
              </legend>
              {colors.map((color) => (
                <label key={color} htmlFor={`color-${color}`} className="circle-ff">
                  <input
                    type="radio"
                    id={`color-${color}`}
                    name="avatar-color"
                    value={color}
                    checked={color === (userProfile?.colour || randomColour)}
                    onChange={() => handleChange('colour', color)}
                    className="visuallyHidden"
                  />
                  <span className="visuallyHidden">{colorsTranslatedNames[color]}</span>
                  {color === (userProfile?.colour || randomColour) && <div className="inner-crc" />}
                </label>
              ))}
            </fieldset>
          </div>
          <label htmlFor="name" className="field-descr not_logged_label">
            {t('R027')}
          </label>
          <input
            className="value_input"
            id="name"
            type="text"
            value={userProfile?.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
          />
          {userProfile?.token && (
            <div className="field-cont">
              <span className="field-descr" aria-hidden="true">
                {t('S018')}
              </span>
              <button
                type="button"
                className="transparent-button link hide-outline"
                onClick={saveToClipboard}
                aria-label={t('S136')}
              >
                app.championhealth.co.uk/code/{userProfile.token}
                <FontAwesomeIcon icon={faShare} className="icon" />
              </button>
            </div>
          )}

          {action === 'invite' && !userProfile?.token && (
            <Button
              isFullWidth
              className="margin-top-24"
              isDisabled={!userProfile?.name || userProfile?.name?.length < 1}
              onClick={() => {
                setInputName(userProfile.name);
                generateLink(userProfile.name, userProfile?.colour || randomColour);
              }}
            >
              {t('S014')}
            </Button>
          )}
          {(action === 'manage' || (action === 'invite' && userProfile?.token)) && (
            <Button
              isFullWidth
              className="margin-top-24"
              onClick={() => {
                setInputName(userProfile.name);
                saveChanges(userProfile);
              }}
            >
              {t('S017')}
            </Button>
          )}
          {action === 'manage' && (
            <Button
              className="transparent-button bold-sm-txt centre margin-top-24"
              onClick={() => setOpenModal(true)}
              variant="ghost"
              color="dark"
              icon={{ faIconDefinition: faTrashAlt }}
            >
              {t('S007')}
            </Button>
          )}
        </>
      )}
    </fieldset>
  );
}
